<template>
  <main class="fm-main">
    <div class="fm-page fm-page--narrow">
      <!-- Loading State -->
      <div v-if="loading" class="ov-state">
        <div class="ov-spinner"></div>
        <h4 class="ov-state-title">Loading Dashboard...</h4>
        <p class="ov-state-sub">Fetching network data and metrics</p>
      </div>

      <!-- Error State -->
      <div v-else-if="networkDown" class="ov-state">
        <div class="ov-state-icon ov-state-icon--err">
          <i class="nc-icon nc-simple-remove"></i>
        </div>
        <h3 class="ov-state-title">Network Connection Failed</h3>
        <p class="ov-state-sub">Unable to connect to the Cloud AM server. Please check your connection and try again.</p>
        <button class="fm-btn fm-btn--primary" @click="fetchNetworkData">
          <i class="nc-icon nc-refresh-02"></i>
          Retry Connection
        </button>
      </div>

      <!-- Main Dashboard Content -->
      <div v-else>
        <!-- Page header -->
        <div class="fm-page-head">
          <div class="fm-page-title">Overview</div>
          <div class="fm-status">
            <span class="fm-status-live"></span>System Online
          </div>
        </div>
        <div class="fm-page-desc">Monitor and manage your secure access infrastructure.</div>

        <!-- KPI tiles -->
        <div class="fm-kpis">
          <div class="fm-kpi ov-kpi" @click="navigateTo('/agents')">
            <div class="fm-kpi-head">
              <span class="fm-kpi-label">Agents</span>
              <i class="nc-icon nc-chart"></i>
            </div>
            <div class="fm-kpi-value">{{ this.agents.length || 0 }}</div>
            <div class="fm-kpi-sub">Total registered</div>
          </div>

          <div class="fm-kpi ov-kpi" @click="navigateTo('/services')">
            <div class="fm-kpi-head">
              <span class="fm-kpi-label">Active Services</span>
              <i class="nc-icon nc-grid-45"></i>
            </div>
            <div class="fm-kpi-value">{{ this.services.length || 0 }}</div>
            <div class="fm-kpi-sub">Currently running</div>
          </div>

          <div class="fm-kpi ov-kpi" @click="navigateTo('/users')">
            <div class="fm-kpi-head">
              <span class="fm-kpi-label">Connected Users</span>
              <i class="nc-icon nc-circle-09"></i>
            </div>
            <div class="fm-kpi-value">{{ this.users.length || 0 }}</div>
            <div class="fm-kpi-sub">Currently online</div>
          </div>

          <div class="fm-kpi">
            <div class="fm-kpi-head">
              <span class="fm-kpi-label">Active Sessions</span>
              <i class="nc-icon nc-spaceship"></i>
            </div>
            <div class="fm-kpi-value">{{ this.clients.length || 0 }}</div>
            <div class="fm-kpi-sub">Live connections</div>
          </div>
        </div>

        <!-- Stats Section - System Health and Security -->
        <div class="ov-grid ov-grid--3">
          <!-- System Health Score -->
          <div class="fm-panel">
            <div class="fm-panel-title">System Health</div>
            <div class="ov-health">
              <svg class="ov-ring" viewBox="0 0 120 120" width="120" height="120">
                <circle class="ov-ring-track" cx="60" cy="60" r="52" />
                <circle class="ov-ring-value" cx="60" cy="60" r="52"
                        :stroke-dasharray="ringCircumference"
                        :stroke-dashoffset="ringOffset" />
                <text class="ov-ring-text" x="60" y="60" text-anchor="middle" dominant-baseline="central">
                  {{ Math.round(stats.trends.systemHealthScore || 0) }}%
                </text>
              </svg>
              <p class="ov-health-label">{{ healthScoreLabel }}</p>
              <small class="ov-health-hint">Based on error rates and performance</small>
            </div>
          </div>

          <!-- Security Stats -->
          <div class="fm-panel">
            <div class="fm-panel-title">Security Overview (24h)</div>
            <div class="ov-stats">
              <div class="ov-stat-item">
                <span class="ov-stat-dot" style="background:#EF4444"></span>
                <div class="ov-stat-content">
                  <div class="ov-stat-value">{{ stats.security.failedAttempts24h || 0 }}</div>
                  <div class="ov-stat-label">Failed Attempts</div>
                </div>
              </div>

              <div class="ov-stat-item">
                <span class="ov-stat-dot" style="background:#F59E0B"></span>
                <div class="ov-stat-content">
                  <div class="ov-stat-value">{{ stats.security.adminActions24h || 0 }}</div>
                  <div class="ov-stat-label">Admin Actions</div>
                </div>
              </div>

              <div class="ov-stat-item">
                <span class="ov-stat-dot" style="background:#3B82F6"></span>
                <div class="ov-stat-content">
                  <div class="ov-stat-value">{{ (stats.security.temporaryAccessExpiring || []).length }}</div>
                  <div class="ov-stat-label">Expiring Access</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Real-time Stats Summary -->
          <div class="fm-panel">
            <div class="fm-panel-title">Real-time Activity</div>
            <div class="ov-stats">
              <div class="ov-stat-item">
                <span class="ov-stat-dot" style="background:#3B82F6"></span>
                <div class="ov-stat-content">
                  <div class="ov-stat-value">{{ stats.realtime.activeUsers || 0 }}</div>
                  <div class="ov-stat-label">Active Users</div>
                </div>
              </div>

              <div class="ov-stat-item">
                <span class="ov-stat-dot" style="background:#10B981"></span>
                <div class="ov-stat-content">
                  <div class="ov-stat-value">{{ stats.realtime.activeServices || 0 }}</div>
                  <div class="ov-stat-label">Active Services</div>
                </div>
              </div>

              <div class="ov-stat-item">
                <span class="ov-stat-dot" style="background:#F59E0B"></span>
                <div class="ov-stat-content">
                  <div class="ov-stat-value">{{ stats.realtime.totalConnections || 0 }}</div>
                  <div class="ov-stat-label">Total Connections</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Active Connections Table -->
        <div class="ov-grid">
          <div class="fm-panel">
            <div class="fm-panel-title">Current Active Connections</div>
              <b-table
                :items="stats.realtime.userConnections || []"
                :fields="connectionFields"
                small
                striped
                responsive
                :per-page="10"
                :current-page="currentPage"
              >
                <template #cell(since)="data">
                  {{ formatDateTime(data.item.since) }}
                </template>
                <template #cell(duration)="data">
                  <b-badge :variant="getDurationVariant(data.item.duration)" class="duration-badge">
                    {{ data.item.duration }}
                  </b-badge>
                </template>
              </b-table>
              <b-pagination
                v-if="(stats.realtime.userConnections || []).length > 10"
                v-model="currentPage"
                :total-rows="(stats.realtime.userConnections || []).length"
                :per-page="10"
                align="center"
                size="sm"
                class="mt-3"
              />
          </div>
        </div>

        <!-- User Activity Ranking -->
        <div class="ov-grid">
          <div class="fm-panel">
            <div class="fm-panel-title">Most Active Users (7 days)</div>
              <b-table
                :items="stats.usage.userActivityRanking || []"
                :fields="userActivityFields"
                small
                striped
                responsive
              >
                <template #cell(lastActivity)="data">
                  {{ formatDateTime(data.item.lastActivity) }}
                </template>
                <template #cell(totalActions)="data">
                  <b-badge variant="primary">{{ data.item.totalActions }}</b-badge>
                </template>
                <template #cell(servicesUsed)="data">
                  <b-badge variant="success">{{ data.item.servicesUsed }}</b-badge>
                </template>
              </b-table>
          </div>
        </div>

        <!-- Auto-refresh notice -->
        <div class="ov-refresh-note">
          <i class="nc-icon nc-refresh-69 mr-1"></i>
          Stats data refreshes automatically every 30 seconds
        </div>

        <!-- Empty State for No Data -->
        <div v-if="!hasAnyData" class="fm-empty ov-empty">
          <h3 class="ov-state-title">Get Started with Cloud AM</h3>
          <p class="ov-state-sub">Begin by setting up your first agent to start monitoring your infrastructure.</p>
          <div class="ov-empty-actions">
            <button class="fm-btn fm-btn--primary" @click="navigateTo('/agents')">
              <i class="nc-icon nc-simple-add"></i>
              Add Agent
            </button>
            <button class="fm-btn fm-btn--ghost" @click="navigateTo('/services')">
              <i class="nc-icon nc-grid-45"></i>
              View Services
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { BCard, BTable, BPagination, BBadge } from "bootstrap-vue";
import StatsCard from 'src/components/Cards/StatsCard.vue';
import apiClient from "@/http";

export default {
  components: {
    StatsCard,
    BCard,
    BTable,
    BPagination,
    BBadge,
  },
  mounted() {
    this.fetchNetworkData();
    this.fetchStats();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  data() {
    return {
      networkData: null,
      agents: [],
      services: [],
      users: [],
      clients: [],
      networkDown: false,
      loading: true,
      currentPage: 1,
      refreshInterval: null,

      stats: {
        realtime: {
          activeUsers: 0,
          activeServices: 0,
          activeAgents: 0,
          totalConnections: 0,
          currentHourConns: 0,
          userConnections: [],
          serviceHealth: []
        },
        usage: {
          hourlyConnections: [],
          serviceUsageRanking: [],
          userActivityRanking: [],
          peakUsageHour: 0,
          averageSessionDuration: "",
          serviceTypeDistribution: {}
        },
        security: {
          failedAttempts24h: 0,
          adminActions24h: 0,
          securityEvents: [],
          accessViolations: 0,
          temporaryAccessExpiring: []
        },
        trends: {
          dailyAuditVolume: [],
          errorRateTrend: [],
          connectionTrend: [],
          systemHealthScore: 0
        }
      },

      connectionFields: [
        { key: 'user', label: 'User', sortable: true },
        { key: 'service', label: 'Service', sortable: true },
        { key: 'since', label: 'Connected Since', sortable: true },
        { key: 'duration', label: 'Duration', sortable: true },
        { key: 'connectionIP', label: 'IP Address', sortable: true }
      ],

      userActivityFields: [
        { key: 'user', label: 'User', sortable: true },
        { key: 'totalActions', label: 'Actions', sortable: true },
        { key: 'servicesUsed', label: 'Services', sortable: true },
        { key: 'lastActivity', label: 'Last Activity', sortable: true }
      ],
    };
  },
  computed: {
    hasAnyData() {
      return this.agents.length > 0 || this.services.length > 0 || this.users.length > 0 || this.clients.length > 0;
    },
    healthScoreClass() {
      const score = this.stats.trends.systemHealthScore || 0;
      if (score >= 90) return 'health-score--excellent';
      if (score >= 75) return 'health-score--good';
      if (score >= 50) return 'health-score--fair';
      return 'health-score--poor';
    },
    healthScoreLabel() {
      const score = this.stats.trends.systemHealthScore || 0;
      if (score >= 90) return 'Excellent';
      if (score >= 75) return 'Good';
      if (score >= 50) return 'Fair';
      return 'Needs Attention';
    },
    ringCircumference() {
      return 2 * Math.PI * 52;
    },
    ringOffset() {
      const score = Math.max(0, Math.min(100, this.stats.trends.systemHealthScore || 0));
      return this.ringCircumference * (1 - score / 100);
    }
  },
  methods: {
    async fetchNetworkData() {
      this.loading = true;
      this.networkDown = false;

      try {
        const response = await apiClient.get('/sessions/network');
        const networkData = response.data;
        this.networkData = networkData;
        this.agents = this.networkData.Nodes.filter(node => node.group === 'agent');
        this.services = this.networkData.Nodes.filter(node => node.group === 'service');
        this.users = this.networkData.Nodes.filter(node => node.group === 'user');
        this.clients = this.networkData.Nodes.filter(node => node.group === 'client');
      } catch (error) {
        console.error('Error fetching network data:', error);
        this.networkDown = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchStats() {
      try {
        const response = await apiClient.get("/stats");
        this.stats = response.data;
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    },
    formatDateTime(dateTime) {
      return new Date(dateTime).toLocaleString();
    },
    getDurationVariant(duration) {
      if (duration.includes('min')) return 'success';
      if (duration.includes('hrs') && parseFloat(duration) < 2) return 'info';
      return 'warning';
    },
    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.fetchStats();
      }, 30000); // Refresh every 30 seconds
    },
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
      }
    },
    navigateTo(route) {
      this.$router.push(route);
    }
  },
}
</script>

<style scoped>
/* ---- Loading / error / empty states (flat) ---- */
.ov-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  gap: 6px;
}
.ov-state-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--fm-text);
  margin: 8px 0 0;
}
.ov-state-sub {
  font-size: 14px;
  color: var(--fm-slate-500);
  margin: 0 0 16px;
  max-width: 420px;
  line-height: 1.6;
}
.ov-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid var(--fm-slate-200);
  border-top-color: var(--fm-slate-600);
  border-radius: 50%;
  animation: ov-spin 0.9s linear infinite;
  margin-bottom: 12px;
}
@keyframes ov-spin {
  to { transform: rotate(360deg); }
}
.ov-state-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 24px;
}
.ov-state-icon--err {
  background: var(--fm-danger-bg);
  color: var(--fm-danger);
}

/* ---- KPI tiles ---- */
.ov-kpi { cursor: pointer; }

/* ---- Panels grid ---- */
.ov-grid { margin-bottom: 16px; }
.ov-grid--3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

/* ---- System health ring ---- */
.ov-health {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.ov-ring { transform: rotate(-90deg); }
.ov-ring-track {
  fill: none;
  stroke: #EEF2F6;
  stroke-width: 10;
}
.ov-ring-value {
  fill: none;
  stroke: #10B981;
  stroke-width: 10;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.5s var(--fm-ease);
}
.ov-ring-text {
  transform: rotate(90deg);
  transform-origin: 60px 60px;
  font-size: 22px;
  font-weight: 600;
  fill: var(--fm-text);
}
.ov-health-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--fm-text);
  margin: 12px 0 2px;
}
.ov-health-hint {
  font-size: 11.5px;
  color: var(--fm-slate-400);
}

/* ---- Stat lists ---- */
.ov-stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--fm-slate-100);
}
.ov-stat-item:last-child { border-bottom: none; }
.ov-stat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}
.ov-stat-value {
  font-size: 20px;
  font-weight: 600;
  color: var(--fm-text);
  line-height: 1.1;
}
.ov-stat-label {
  font-size: 12.5px;
  color: var(--fm-slate-500);
}

/* ---- Refresh note ---- */
.ov-refresh-note {
  text-align: center;
  color: var(--fm-slate-400);
  font-size: 12px;
  margin: 8px 0 16px;
}

/* ---- Empty state ---- */
.ov-empty {
  margin-top: 8px;
}
.ov-empty-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 14px;
}

/* Table tweak */
.duration-badge {
  font-size: 0.8rem;
  padding: 4px 8px;
}

@media (max-width: 992px) {
  .ov-grid--3 { grid-template-columns: 1fr; }
  .fm-kpis { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .fm-kpis { grid-template-columns: 1fr; }
}
</style>
